/**
 * Build display URLs from Cloudflare Stream / Images IDs.
 * Persist stable image ids in `events.images` JSON; the `events.image` column may denormalize
 * the viewer-variant URL for fast reads.
 */

import { cfImage } from "@/lib/cf-image"

export function isHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s.trim())
}

/** Cloudflare Images delivery URL, or null if hash is not configured. */
/** Variant names from env; defaults both `public` until you set distinct viewer vs thumb in Cloudflare + env. */
/** Full-quality variant: event hero, album grids, cards — default delivery for visible surfaces. */
export const CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT =
  process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_EVENT_VIEWER_VARIANT?.trim() ||
  "public"

/** Small variant: lightbox blur shell, picker strip, notifications — not final card/cover fills. */
export const CLOUDFLARE_EVENT_COVER_THUMB_VARIANT =
  process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_EVENT_THUMB_VARIANT?.trim() ||
  "public"

export const CLOUDFLARE_EVENT_COVER_DOWNLOAD_VARIANT =
  process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_EVENT_DOWNLOAD_VARIANT?.trim() ||
  CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT

/** When false, both grid and lightbox use the same delivery variant until env separates them. */
export function cloudflareImageViewerThumbVariantsAreDistinct(): boolean {
  return (
    CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT !== CLOUDFLARE_EVENT_COVER_THUMB_VARIANT
  )
}

export function cloudflareImageDeliveryUrl(
  imageId: string,
  variant = "public",
): string | null {
  const id = imageId.trim()
  if (!id) return null
  try {
    return cfImage(id, variant)
  } catch {
    // Client-rendered paths may not have server-only env vars; fail closed instead of crashing UI.
    return null
  }
}

/** Deterministic delivery URL for a Cloudflare Images id and variant name. */
export function buildCloudflareImageUrl(imageId: string, variant: string): string {
  return cloudflareImageDeliveryUrl(imageId.trim(), variant.trim()) ?? ""
}

/** Removes `v` cache-buster so URLs can be compared or re-busted. */
export function stripImageCacheBustParam(url: string): string {
  const t = url.trim()
  if (!t || t.startsWith("blob:") || t.startsWith("data:")) return t
  if (!t.includes("?") && !t.includes("&v=")) return t
  if (t.startsWith("/")) {
    const [path, query] = t.split("?")
    if (!query) return path ?? t
    const sp = new URLSearchParams(query)
    sp.delete("v")
    const q = sp.toString()
    return q ? `${path}?${q}` : (path ?? t)
  }
  try {
    const u = new URL(t)
    u.searchParams.delete("v")
    const q = u.searchParams.toString()
    return `${u.origin}${u.pathname}${q ? `?${q}` : ""}`
  } catch {
    return t
  }
}

/** Appends or replaces `v` for CDN / browser cache busting (post-upload freshness). */
export function appendCacheBustToImageUrl(
  url: string,
  version: number | string,
): string {
  const base = stripImageCacheBustParam(url)
  const u = base.trim()
  if (!u || u.startsWith("blob:") || u.startsWith("data:")) return u
  /** `next/image` local assets — no query bust (Next 16 `localPatterns`); change filename to bust cache. */
  if (u.startsWith("/")) return u
  const v = encodeURIComponent(String(version))
  return u.includes("?") ? `${u}&v=${v}` : `${u}?v=${v}`
}

/**
 * Identity for "same underlying image" (ignores `v=` and other harmless query churn).
 */
export function stableImageUrlIdentity(url: string): string {
  const t = url.trim()
  if (!t) return ""
  if (t.startsWith("blob:") || t.startsWith("data:")) return t
  if (t.startsWith("/")) {
    const pathOnly = stripImageCacheBustParam(t).split("?")[0] ?? t
    return pathOnly
  }
  try {
    const u = new URL(t)
    u.searchParams.delete("v")
    const q = u.searchParams.toString()
    return `${u.host}${u.pathname}${q ? `?${q}` : ""}`
  } catch {
    return stripImageCacheBustParam(t).split("?")[0] ?? t
  }
}

/** Preload before swapping `src` (avoids blank frame when replacing blob with CDN). */
export function preloadImageUrl(url: string): Promise<void> {
  const u = url.trim()
  if (!u) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => resolve()
    img.onerror = () => reject(new Error("preload_failed"))
    img.src = u
  })
}

/**
 * If `ref` is already an http(s) URL, return as-is; otherwise treat as a Cloudflare Images id.
 */
export function resolveImageRefToDisplayUrl(ref: string): string {
  const t = ref.trim()
  if (!t) return ""
  if (isHttpUrl(t)) return t
  return cloudflareImageDeliveryUrl(t) ?? ""
}

export type StreamUrlOptions = {
  /** Signed playback token (JWT). When set, appended for private/signed videos. */
  token?: string
}

/** Cloudflare Stream iframe player URL (uid only; optional signed token query). */
export function getStreamIframeUrl(
  uid: string,
  opts?: StreamUrlOptions,
): string {
  const id = uid.trim()
  if (!id) return ""
  const base = `https://iframe.videodelivery.net/${id}`
  const token = opts?.token?.trim()
  if (!token) return base
  return `${base}?${new URLSearchParams({ token })}`
}

/**
 * Public HLS manifest URL. For signed videos, pass `token` and use it as the playback key
 * (Cloudflare expects the token in place of the uid in the path).
 */
export function getStreamHlsUrl(uid: string, opts?: StreamUrlOptions): string {
  const id = uid.trim()
  const key = (opts?.token?.trim() || id).trim()
  if (!key) return ""
  return `https://videodelivery.net/${key}/manifest/video.m3u8`
}

/** Default poster / thumbnail URL (optional `time` e.g. `5s` for sprite). */
export function getStreamThumbnailUrl(
  uid: string,
  opts?: StreamUrlOptions & { time?: string },
): string {
  const id = uid.trim()
  const key = (opts?.token?.trim() || id).trim()
  if (!key) return ""
  const path = `https://videodelivery.net/${key}/thumbnails/thumbnail.jpg`
  const time = opts?.time?.trim()
  if (!time) return path
  return `${path}?${new URLSearchParams({ time })}`
}

/** @deprecated Use {@link getStreamIframeUrl} */
export function cloudflareStreamIframeUrl(videoUid: string): string {
  return getStreamIframeUrl(videoUid)
}

/** Public R2 object URL when the bucket is exposed via a custom/public domain. */
export function r2PublicObjectUrl(key: string): string | null {
  const base = process.env.R2_PUBLIC_BASE_URL?.trim().replace(/\/$/, "")
  if (!base) return null
  const path = key.replace(/^\//, "").split("/").map(encodeURIComponent).join("/")
  return `${base}/${path}`
}

/*
 * Example (Route Handler or server action):
 *
 *   const iframeSrc = getStreamIframeUrl(videoUid)
 *   const hls = getStreamHlsUrl(videoUid)
 *   const poster = getStreamThumbnailUrl(videoUid, { time: "2s" })
 *
 * With signed playback (token from Stream `/token` API or local signing key):
 *
 *   const iframeSrc = getStreamIframeUrl(uid, { token })
 */
